import { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Download, Printer, Filter, Calendar, Users, Home as HomeIcon,
  Wallet, RefreshCw, Search, CheckCircle2, ChevronRight, AlertTriangle
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, todayIST } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const REPORT_CATEGORIES = {
  patients: [
    { id: "active-patients", label: "Active Patients" },
    { id: "paused-patients", label: "Paused Patients" },
    { id: "completed-patients", label: "Completed Patients" },
    { id: "discontinued-patients", label: "Discontinued Patients" },
    { id: "patient-credit", label: "Patient Credit Balance" },
    { id: "patient-due", label: "Patient Outstanding Due" },
    { id: "low-credit", label: "Low Credit Warning" },
    { id: "packages", label: "Custom Packages Status" },
    { id: "new-patients", label: "New Patient Registrations" },
  ],
  clinic: [
    { id: "daily-attendance", label: "Daily Clinic Attendance" },
    { id: "clinic-collection", label: "Clinic Collections" },
    { id: "payment-methods", label: "Payment Methods Breakdown" },
    { id: "follow-ups", label: "Clinic Follow-ups Log" },
  ],
  home: [
    { id: "home-visits", label: "Home Visits Log" },
    { id: "home-collection", label: "Home Collections" },
    { id: "physio-workload", label: "Physiotherapist Workload" },
  ],
  finance: [
    { id: "daily-collection", label: "Daily Total Collection" },
    { id: "service-revenue", label: "Delivered Service Revenue" },
    { id: "payment-methods", label: "Payment Modes (Cash/UPI/Bank)" },
    { id: "expenses", label: "Expenses & Petrol Log" },
  ],
};

export default function Reports() {
  const [activeTab, setActiveTab] = useState("patients");
  const [selectedReport, setSelectedReport] = useState("active-patients");
  const [dateFrom, setDateFrom] = useState(`${todayIST().slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(todayIST());
  const [search, setSearch] = useState("");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  // When switching category tabs, select the first report of that category
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    const firstRep = REPORT_CATEGORIES[tabKey]?.[0]?.id;
    if (firstRep) {
      setSelectedReport(firstRep);
    }
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/${selectedReport}`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setReportData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedReport, dateFrom, dateTo]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Export CSV
  const handleExportCSV = () => {
    const url = `${api.defaults.baseURL || ""}/reports/${selectedReport}/export?date_from=${dateFrom}&date_to=${dateTo}`;
    window.open(url, "_blank");
  };

  // Print Report
  const handlePrint = () => {
    window.print();
  };

  const columns = reportData?.columns || [];
  const meta = reportData?.metadata || {};

  // Filter rows by search
  const filteredRows = useMemo(() => {
    const list = reportData?.rows || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((r) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [reportData?.rows, search]);

  return (
    <div className="space-y-5">
      {/* Printable Header (Visible only during window.print) */}
      <div className="hidden print:block mb-6 border-b pb-4">
        <h1 className="text-2xl font-bold text-slate-900">BK Spine Care Physiotherapy Clinic</h1>
        <p className="text-sm font-semibold text-slate-700">
          Report: {selectedReport.replace("-", " ").toUpperCase()}
        </p>
        <p className="text-xs text-slate-500">
          Period: {dateFrom} to {dateTo} · Generated on {new Date().toLocaleString("en-IN")}
        </p>
      </div>

      <div className="print:hidden">
        <PageHeader
          title="Reports & Analytics"
          subtitle="Generate, filter, print and export clinic, home service, patient, and financial reports"
          testid="reports-page"
          action={
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handlePrint}
                className="gap-1.5 bg-white"
              >
                <Printer className="h-4 w-4" /> Print / PDF
              </Button>
              <Button
                size="sm"
                onClick={handleExportCSV}
                className="gap-1.5 bg-bk-green hover:bg-emerald-700 text-white"
              >
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            </div>
          }
        />
      </div>

      {/* Category Tabs */}
      <div className="print:hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-xl bg-slate-100 p-1">
            <TabsTrigger value="patients" className="text-xs font-semibold">
              <Users className="h-3.5 w-3.5 mr-1" /> Patients
            </TabsTrigger>
            <TabsTrigger value="clinic" className="text-xs font-semibold">
              <Calendar className="h-3.5 w-3.5 mr-1" /> Clinic
            </TabsTrigger>
            <TabsTrigger value="home" className="text-xs font-semibold">
              <HomeIcon className="h-3.5 w-3.5 mr-1" /> Home Service
            </TabsTrigger>
            <TabsTrigger value="finance" className="text-xs font-semibold">
              <Wallet className="h-3.5 w-3.5 mr-1" /> Financial
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filter and Report Selector Bar */}
      <Card className="p-3.5 bg-slate-50/90 border-slate-200 print:hidden">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Report Dropdown for Active Category */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Report:</span>
            <Select value={selectedReport} onValueChange={setSelectedReport}>
              <SelectTrigger className="h-8 text-xs w-64 bg-white font-medium">
                <SelectValue placeholder="Select Report" />
              </SelectTrigger>
              <SelectContent>
                {(REPORT_CATEGORIES[activeTab] || []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Pickers */}
            <div className="flex items-center gap-1 ml-2">
              <span className="text-xs font-semibold text-slate-500">From:</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 text-xs w-32 bg-white"
              />
              <span className="text-xs font-semibold text-slate-500">To:</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 text-xs w-32 bg-white"
              />
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={loadReport}
              className="h-8 px-2.5 text-xs bg-white gap-1"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Apply
            </Button>
          </div>

          {/* Quick Search */}
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search table data..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs bg-white"
            />
          </div>
        </div>
      </Card>

      {/* Report Data Table */}
      <Card className="overflow-hidden border-slate-200 shadow-sm bg-white">
        {loading ? (
          <div className="p-12">
            <Loading label="Generating report data..." />
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={FileText}
              title="No records found"
              subtitle="No records matched the selected report and date range filters."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-12 text-xs font-bold text-slate-500">#</TableHead>
                  {columns.map((col) => (
                    <TableHead key={col} className="text-xs font-bold text-slate-700 capitalize">
                      {col.replace(/_/g, " ")}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row, idx) => (
                  <TableRow key={idx} className="hover:bg-slate-50/80">
                    <TableCell className="text-xs text-slate-400 font-mono">{idx + 1}</TableCell>
                    {columns.map((col) => {
                      const val = row[col];
                      const isAmount =
                        typeof val === "number" &&
                        (col.includes("amount") || col.includes("balance") || col.includes("collection") || col.includes("fee") || col.includes("revenue") || col.includes("due") || col.includes("paid"));

                      return (
                        <TableCell key={col} className="text-xs text-slate-700">
                          {isAmount ? (
                            <span className="font-semibold text-slate-900 tabular-nums">
                              {inr(val)}
                            </span>
                          ) : val === null || val === undefined || val === "" ? (
                            <span className="text-slate-300">-</span>
                          ) : String(val) === "Active" || String(val) === "Credit" ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]">
                              {String(val)}
                            </Badge>
                          ) : String(val) === "Due" ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[11px]">
                              {String(val)}
                            </Badge>
                          ) : (
                            String(val)
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
